export const ANCHOR_PRICE_GENERATION_STATUS = {
  APPLIED: 'applied',
  GENERATING: 'generating',
  IDLE: 'idle',
  READY: 'ready',
  STALE: 'stale',
} as const;

export type AnchorPriceGenerationStatus =
  typeof ANCHOR_PRICE_GENERATION_STATUS[keyof typeof ANCHOR_PRICE_GENERATION_STATUS];

// Fast-completion window: if `regenerated` arrives within this many milliseconds of `regenerating`
// the banner auto-applies (no button, just an "applied" confirmation the buyer can dismiss).
export const ANCHOR_PRICE_AUTO_APPLY_WINDOW_MS = 2000;

let status: AnchorPriceGenerationStatus = ANCHOR_PRICE_GENERATION_STATUS.IDLE;
let generatingStartedAt: number | null = null;
let hasUnappliedGeneration = false;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach(listener => listener());
};

export const anchorPriceGenerationStore = {
  getGeneratingStartedAt: (): number | null => generatingStartedAt,
  /**
   * True while regenerated prices exist server-side that this client has not yet pulled into the
   * grid — set on READY, cleared once they are applied (APPLIED) or the prompt is acted on or
   * dismissed (IDLE). STALE deliberately preserves it: a trigger-field edit invalidates the
   * prices but does not make them disappear, so discarding that edit must restore the apply
   * prompt rather than drop it.
   */
  getHasUnappliedGeneration: (): boolean => hasUnappliedGeneration,
  getStatus: (): AnchorPriceGenerationStatus => status,
  markGenerating: (): void => {
    if (status === ANCHOR_PRICE_GENERATION_STATUS.GENERATING) return;
    status = ANCHOR_PRICE_GENERATION_STATUS.GENERATING;
    generatingStartedAt = Date.now();
    notify();
  },
  // Stale is reachable from any settled status (IDLE, READY, APPLIED) — READY/APPLIED linger
  // after a save regenerates prices until the buyer dismisses the banner, and trigger-field
  // edits made while they linger still invalidate the prices. Only an in-flight regeneration
  // blocks the transition: the grid is regen-locked and fresh prices are already on the way.
  markStale: (): void => {
    if (status === ANCHOR_PRICE_GENERATION_STATUS.GENERATING) return;
    if (status === ANCHOR_PRICE_GENERATION_STATUS.STALE) return;
    status = ANCHOR_PRICE_GENERATION_STATUS.STALE;
    notify();
  },
  setStatus: (next: AnchorPriceGenerationStatus): void => {
    if (status === next) return;
    status = next;
    if (next === ANCHOR_PRICE_GENERATION_STATUS.IDLE) generatingStartedAt = null;
    if (next === ANCHOR_PRICE_GENERATION_STATUS.READY) hasUnappliedGeneration = true;
    if (next === ANCHOR_PRICE_GENERATION_STATUS.APPLIED || next === ANCHOR_PRICE_GENERATION_STATUS.IDLE) {
      hasUnappliedGeneration = false;
    }
    notify();
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
};
